import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { dashFetch } from "@/api/client";
import { friendlyAuthError } from "@/auth/authErrors";
import { BRAND_NAME } from "@/brand";
import { IconClose } from "@/components/icons/PageActionIcons";
import "./dns-instructions-email-modal.css";

export type DnsEmailKind = "txt" | "a";

type Props = {
  open: boolean;
  kind: DnsEmailKind;
  domain: string;
  defaultEmail: string;
  recordHost?: string;
  recordType?: string;
  recordValue?: string;
  proxyIps?: string[];
  onClose: () => void;
  onSent?: (email: string) => void;
};

function isValidEmail(email: string): boolean {
  const e = email.trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
}

export default function DnsInstructionsEmailModal({
  open,
  kind,
  domain,
  defaultEmail,
  recordHost = "",
  recordType = "TXT",
  recordValue = "",
  proxyIps = [],
  onClose,
  onSent
}: Props) {
  const [email, setEmail] = useState(defaultEmail);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const copy = useMemo(() => {
    if (kind === "a") {
      return {
        title: "Send A record instructions",
        lead: (
          <>
            We&apos;ll email the A record IP{proxyIps.length > 1 ? "s" : ""} for{" "}
            <span className="mono">{domain}</span> so you or a colleague can update DNS.
          </>
        )
      };
    }
    return {
      title: "Send DNS instructions",
      lead: (
        <>
          We&apos;ll email the TXT record details for <span className="mono">{domain}</span> so you or a
          colleague can add them at your DNS provider.
        </>
      )
    };
  }, [kind, domain, proxyIps.length]);

  useEffect(() => {
    if (!open) return;
    setEmail(defaultEmail);
    setError("");
    setBusy(false);
    const t = window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [open, defaultEmail]);

  useEffect(() => {
    if (!open) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onClose]);

  const send = useCallback(async () => {
    const to = email.trim();
    if (!isValidEmail(to)) {
      setError("Enter a valid email address.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const body =
        kind === "a"
          ? { domain, to, type: "a_record" }
          : {
              domain,
              to,
              type: "txt",
              recordHost,
              recordType,
              recordValue
            };
      const resp = await dashFetch<{ ok?: boolean; message?: string; error?: string }>(
        "/dashboard/api/tenants/verification/email",
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(body),
          site: domain
        }
      );
      if (!resp?.ok) {
        setError(
          friendlyAuthError(resp?.error, { message: resp?.message }) ||
            "Could not send email. Try again."
        );
        return;
      }
      onSent?.(to);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not send email.");
    } finally {
      setBusy(false);
    }
  }, [domain, email, kind, onClose, onSent, recordHost, recordType, recordValue]);

  if (!open) return null;

  return createPortal(
    <div className="dnsEmailModal on" role="presentation">
      <div className="dnsEmailModal__backdrop" onClick={() => !busy && onClose()} />
      <div className="dnsEmailModal__card" role="dialog" aria-modal="true" aria-labelledby="dnsEmailModalTitle">
        <div className="dnsEmailModal__header">
          <div>
            <div className="dnsEmailModal__eyebrow">{BRAND_NAME}</div>
            <h2 id="dnsEmailModalTitle" className="dnsEmailModal__title">
              {copy.title}
            </h2>
          </div>
          <button
            type="button"
            className="iconBtn"
            title="Close"
            disabled={busy}
            onClick={onClose}
          >
            <IconClose />
          </button>
        </div>

        <p className="dnsEmailModal__lead">{copy.lead}</p>

        <label className="dnsEmailModal__field">
          <span className="dnsEmailModal__label">Recipient email</span>
          <input
            ref={inputRef}
            type="email"
            className="settingsInput"
            autoComplete="email"
            spellCheck={false}
            value={email}
            disabled={busy}
            placeholder="name@company.com"
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) setError("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") void send();
            }}
          />
          <span className="dnsEmailModal__hint">
            Defaults to your account email; change it if someone else manages DNS.
          </span>
        </label>

        {error ? (
          <div className="dnsEmailModal__error" role="alert">
            {error}
          </div>
        ) : null}

        <div className="dnsEmailModal__actions">
          <button type="button" className="btn" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="primaryBtn" disabled={busy} onClick={() => void send()}>
            {busy ? "Sending…" : "Send email"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
